import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Paper, Button } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Hero from '../../sections/Hero';
import QuoteRequestForm from '../../sections/QuoteRequestForm';

// Phone number formatting helper
const formatPhoneNumber = (phone: string): string => {
  if (phone.length !== 10) return phone;
  return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`;
};

// Contact information constants
const PHONE_NUMBERS = [
  { raw: '9202558952', formatted: '(920) 255-8952' },
  { raw: '9202654729', formatted: '(920) 265-4729' },
];

const ContactPage: React.FC = () => {

  return (
    <>
      <Helmet>
        <title>Contact Us</title>
        <meta
          name="description"
          content="Contact JBR Delivery LLC for delivery, junk removal, and moving services in Northeast Wisconsin. Get a quote or schedule a service today."
        />
      </Helmet>

      <Hero 
        title="Contact Us"
        subtitle="Get in touch with our team for quotes, questions, or scheduling"
        backgroundImage="/assets/images/contact-bg.jpg"
        showQuickForm={false}
      />

      <Box component="section" py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid item xs={12} lg={4}>
              <Typography variant="h2" component="h2" gutterBottom sx={{ mb: 2 }}>
                Get In Touch
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 5 }}>
                Whether you need a quote, have questions about our services, or want to schedule a pickup or delivery, our team is ready to help.
              </Typography>
              
              <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
                <Box display="flex" alignItems="flex-start">
                  <PhoneIcon fontSize="large" sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Phone
                    </Typography>
                    {PHONE_NUMBERS.map((phone, index) => (
                      <Typography key={phone.raw} variant="body1" sx={{ mb: index === 0 ? 1 : 0 }}>
                        <Button 
                          href={`tel:${phone.raw}`} 
                          color="primary" 
                          sx={{ p: 0, textTransform: 'none', minWidth: 0 }}
                        >
                          {phone.formatted}
                        </Button>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Paper>
              
              <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
                <Box display="flex" alignItems="flex-start">
                  <EmailIcon fontSize="large" sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                      <Button
                        href="mailto:jbrdeliveryllc@gmail.com"
                        color="primary"
                        sx={{ p: 0, textTransform: 'none', minWidth: 0, wordBreak: 'break-word' }}
                      >
                        jbrdeliveryllc@gmail.com
                      </Button>
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              
              <Paper elevation={2} sx={{ p: 4 }}>
                <Box display="flex" alignItems="flex-start">
                  <LocationOnIcon fontSize="large" sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Service Area
                    </Typography>
                    <Typography variant="body1">
                      Green Bay and Northeast Wisconsin
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} lg={8}>
              {/* The QuoteRequestForm component has its own internal grid layout */}
              {/* so we give it more space in the parent grid */}
              <QuoteRequestForm />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ContactPage;
